import type {
  FailureReason,
  LevelDef,
  NodeDef,
  SimEvent,
  SimResult,
  WireDef,
  WireType,
} from './types';

const MAX_TICKS = 200;
const DEFAULT_DELAY_FOR: Record<WireType, number> = {
  normal: 1,
  delay: 3,
  directional: 1,
  locked: 1,
  burn: 1,
  forbidden: 1,
};

interface NodeRuntime extends NodeDef {
  charge: number;
  collapsed: boolean;
  lastChargedTick: number;
  shieldedBy: Set<string>;
  effThreshold: number;
  effDecay: number;
}

interface WireRuntime extends WireDef {
  open: boolean;
  burned: boolean;
  effDelay: number;
  effType: WireType;
}

interface PulseInFlight {
  wireId: string;
  fromId: string;
  toId: string;
  emitTick: number;
  arriveTick: number;
}

/**
 * Run the cascade. Pure function of (level, starterId). Same inputs always
 * produce the same event log and outcome — the renderer just plays it back.
 */
export function simulate(level: LevelDef, starterId: string): SimResult {
  const nodes = new Map<string, NodeRuntime>();
  const wires = new Map<string, WireRuntime>();
  const adjacency = new Map<string, string[]>();

  for (const n of level.nodes) {
    const defaultThreshold = n.type === 'protected' ? 1 : 1;
    nodes.set(n.id, {
      ...n,
      charge: 0,
      collapsed: false,
      lastChargedTick: -1,
      shieldedBy: new Set(),
      effThreshold: n.threshold ?? defaultThreshold,
      effDecay: n.decay ?? Number.POSITIVE_INFINITY,
    });
    adjacency.set(n.id, []);
  }

  for (const w of level.wires) {
    const type = w.type ?? 'normal';
    wires.set(w.id, {
      ...w,
      type,
      open: type !== 'locked',
      burned: false,
      effDelay: w.delay ?? DEFAULT_DELAY_FOR[type],
      effType: type,
    });
    adjacency.get(w.a)?.push(w.id);
    adjacency.get(w.b)?.push(w.id);
  }

  // Wire up shield → protected relationships
  for (const n of nodes.values()) {
    if (n.type === 'shield' && n.shields) {
      for (const protectedId of n.shields) {
        nodes.get(protectedId)?.shieldedBy.add(n.id);
      }
    }
  }

  const events: SimEvent[] = [];
  const pulses: PulseInFlight[] = [];

  const starter = nodes.get(starterId);
  if (!starter) {
    return {
      outcome: 'fail',
      starterId,
      events: [],
      totalTicks: 0,
      failure: { kind: 'chain_died' },
      finalCharge: {},
      collapsed: new Set(),
    };
  }

  events.push({ kind: 'tap', tick: 0, nodeId: starterId });

  let tick = 0;
  let failure: FailureReason | undefined;

  const collapseNode = (nodeId: string, atTick: number): boolean => {
    const node = nodes.get(nodeId)!;
    if (node.collapsed) return true;
    node.collapsed = true;
    events.push({ kind: 'node_collapse', tick: atTick, nodeId });

    // Relay: open locked wires
    if (node.type === 'relay' && node.unlocks) {
      for (const wireId of node.unlocks) {
        const w = wires.get(wireId);
        if (w && !w.open) {
          w.open = true;
          events.push({ kind: 'wire_open', tick: atTick, wireId, byRelayId: nodeId });
        }
      }
    }

    // Shield: drop protection
    if (node.type === 'shield' && node.shields) {
      const dropped: string[] = [];
      for (const protectedId of node.shields) {
        const p = nodes.get(protectedId);
        if (p && p.shieldedBy.has(nodeId)) {
          p.shieldedBy.delete(nodeId);
          dropped.push(protectedId);
        }
      }
      if (dropped.length > 0) {
        events.push({ kind: 'shield_drop', tick: atTick, shieldId: nodeId, protectedIds: dropped });
      }
    }

    // Emit pulses on all eligible outgoing wires
    const factor = node.type === 'splitter' ? Math.max(1, node.splitFactor ?? 2) : 1;
    const wireIds = adjacency.get(nodeId) ?? [];
    for (const wireId of wireIds) {
      const wire = wires.get(wireId)!;
      if (!wire.open || wire.burned) continue;

      // Directional and forbidden wires only conduct from a → b.
      // (Without this, a downstream node collapsing would fire BACKWARD
      // through any forbidden wire it happens to touch and instantly fail.)
      if ((wire.effType === 'directional' || wire.effType === 'forbidden') && wire.a !== nodeId) continue;

      const otherId = wire.a === nodeId ? wire.b : wire.a;
      const target = nodes.get(otherId)!;
      // Skip emitting to already-collapsed nodes (cleaner cascade visuals)
      if (target.collapsed) continue;

      const arriveTick = atTick + wire.effDelay;
      for (let i = 0; i < factor; i++) {
        pulses.push({ wireId, fromId: nodeId, toId: otherId, emitTick: atTick, arriveTick });
        events.push({ kind: 'pulse_emit', tick: atTick, wireId, fromId: nodeId, toId: otherId, arriveTick });
      }

      if (wire.effType === 'burn') {
        wire.burned = true;
        events.push({ kind: 'wire_burn', tick: atTick, wireId });
      }
    }

    return true;
  };

  // Tick 0: starter collapses immediately
  collapseNode(starterId, 0);

  // Main loop
  for (tick = 1; tick <= MAX_TICKS; tick++) {
    // Decay step (before arrivals — last cycle's leftover charge drains first)
    for (const node of nodes.values()) {
      if (node.collapsed || node.charge <= 0) continue;
      if (node.effDecay === Number.POSITIVE_INFINITY) continue;
      if (tick - node.lastChargedTick >= node.effDecay) {
        events.push({ kind: 'charge_decay', tick, nodeId: node.id });
        node.charge = 0;
      }
    }

    // Pull arrivals for this tick
    const arrivals: PulseInFlight[] = [];
    for (let i = pulses.length - 1; i >= 0; i--) {
      if (pulses[i].arriveTick === tick) {
        arrivals.push(pulses[i]);
        pulses.splice(i, 1);
      }
    }

    // Check forbidden wires first (any traversal is fatal)
    for (const p of arrivals) {
      const wire = wires.get(p.wireId)!;
      if (wire.effType === 'forbidden') {
        events.push({ kind: 'forbidden_trigger', tick, wireId: p.wireId });
        failure = { kind: 'forbidden_trigger', wireId: p.wireId };
        break;
      }
    }
    if (failure) break;

    // Group arrivals by target node
    const byTarget = new Map<string, PulseInFlight[]>();
    for (const p of arrivals) {
      const list = byTarget.get(p.toId) ?? [];
      list.push(p);
      byTarget.set(p.toId, list);
    }

    // Collapses queued so we don't iterate map while mutating it
    const collapseQueue: string[] = [];

    for (const [targetId, ps] of byTarget) {
      const target = nodes.get(targetId)!;

      // Already-collapsed nodes absorb pulses harmlessly
      if (target.collapsed) {
        for (const p of ps) {
          events.push({
            kind: 'pulse_arrive', tick, wireId: p.wireId, toId: targetId,
            chargeAfter: 0, thresholdAt: target.effThreshold,
          });
        }
        continue;
      }

      // Shielded nodes (any type) absorb pulses harmlessly until shield drops
      if (target.shieldedBy.size > 0) {
        for (const p of ps) {
          events.push({
            kind: 'pulse_arrive', tick, wireId: p.wireId, toId: targetId,
            chargeAfter: 0, thresholdAt: target.effThreshold,
          });
        }
        continue;
      }

      // Accumulate charge
      target.charge += ps.length;
      target.lastChargedTick = tick;
      let runningCharge = target.charge - ps.length;
      for (const p of ps) {
        runningCharge += 1;
        events.push({
          kind: 'pulse_arrive', tick, wireId: p.wireId, toId: targetId,
          chargeAfter: runningCharge, thresholdAt: target.effThreshold,
        });
      }

      // Core: log every arriving pulse separately so the diagnostic can describe the window
      if (target.type === 'core') {
        events.push({ kind: 'core_pulse', tick, coreId: targetId, pulsesInWindow: target.charge });
      }

      // Threshold check
      if (target.charge >= target.effThreshold) {
        // Protected node hitting threshold = overload = fail
        if (target.type === 'protected') {
          events.push({
            kind: 'protected_overload', tick, nodeId: targetId, byWireId: ps[ps.length - 1].wireId,
          });
          failure = { kind: 'protected_overload', nodeId: targetId };
          break;
        }
        collapseQueue.push(targetId);
      }
    }

    if (failure) break;

    for (const id of collapseQueue) {
      collapseNode(id, tick);
    }

    // Termination: nothing left to happen
    const anyCharged = [...nodes.values()].some((n) => !n.collapsed && n.charge > 0);
    if (pulses.length === 0 && !anyCharged) break;
  }

  const finalCharge: Record<string, number> = {};
  const collapsed = new Set<string>();
  for (const n of nodes.values()) {
    finalCharge[n.id] = n.charge;
    if (n.collapsed) collapsed.add(n.id);
  }

  // If we already failed during the sim, return now
  if (failure) {
    return { outcome: 'fail', starterId, events, totalTicks: tick, failure, finalCharge, collapsed };
  }

  // Post-sim evaluation
  const diag = diagnose(level, events, nodes, collapsed);
  if (diag) {
    return { outcome: 'fail', starterId, events, totalTicks: tick, failure: diag, finalCharge, collapsed };
  }

  return { outcome: 'win', starterId, events, totalTicks: tick, finalCharge, collapsed };
}

function diagnose(
  level: LevelDef,
  events: SimEvent[],
  nodes: Map<string, NodeRuntime>,
  collapsed: Set<string>,
): FailureReason | undefined {
  const arrivalCount = (nodeId: string) =>
    events.reduce((n, e) => (e.kind === 'pulse_arrive' && e.toId === nodeId ? n + 1 : n), 0);

  // Required nodes not collapsed
  for (const reqId of level.objective.requiredNodeIds) {
    if (collapsed.has(reqId)) continue;
    const node = nodes.get(reqId);
    if (!node) continue;
    const total = arrivalCount(reqId);
    if (total === 0) {
      return { kind: 'required_not_reached', nodeId: reqId };
    }
    return {
      kind: 'required_under_threshold',
      nodeId: reqId,
      received: total,
      needed: node.effThreshold,
    };
  }

  // Core not collapsed
  if (level.objective.coreNodeId && !collapsed.has(level.objective.coreNodeId)) {
    const core = nodes.get(level.objective.coreNodeId)!;
    const total = arrivalCount(core.id);
    if (total > 0 && core.shieldedBy.size > 0) {
      return { kind: 'core_shielded', coreId: core.id, shieldIds: [...core.shieldedBy] };
    }
    if (core.effThreshold > 1) {
      return {
        kind: 'core_under_threshold',
        coreId: core.id,
        received: total,
        needed: core.effThreshold,
        windowTicks: core.effDecay === Number.POSITIVE_INFINITY ? 0 : core.effDecay,
      };
    }
    return { kind: 'core_not_collapsed', coreId: core.id };
  }

  return undefined;
}

import { t } from '../i18n';

/**
 * Concrete, action-oriented advice paired with each failure kind. Surfaced
 * under the failure explanation so a stuck player knows what to try next.
 */
export function suggestFix(failure: FailureReason): string {
  switch (failure.kind) {
    case 'protected_overload':  return t('fix_protected_overload');
    case 'forbidden_trigger':   return t('fix_forbidden_trigger');
    case 'required_not_reached': return t('fix_required_not_reached');
    case 'required_under_threshold': return t('fix_required_under_threshold');
    case 'core_not_collapsed':  return t('fix_core_not_collapsed');
    case 'core_under_threshold': return t('fix_core_under_threshold');
    case 'core_shielded':       return t('fix_core_shielded');
    case 'chain_died':          return t('fix_chain_died');
  }
}

/** Human-readable failure explanation for the result overlay. */
export function explainFailure(_level: LevelDef, failure: FailureReason): string {
  switch (failure.kind) {
    case 'protected_overload':  return t('fail_protected_overload');
    case 'forbidden_trigger':   return t('fail_forbidden_trigger');
    case 'required_not_reached': return t('fail_required_not_reached');
    case 'required_under_threshold': return t('fail_required_under_threshold');
    case 'core_not_collapsed':  return t('fail_core_not_collapsed');
    case 'core_under_threshold': return t('fail_core_under_threshold');
    case 'core_shielded':       return t('fail_core_shielded');
    case 'chain_died':          return t('fail_chain_died');
  }
}
